<!-- Clicking outside closes. The keyboard equivalent is Escape, heard on the
     document while the dialog is open (see onEscape). It was bound on the
     dialog, so it did nothing unless focus was already inside, and it never
     was (28 Sep 508 rescan). -->
<div class="gh-scrim" (click)="closed.emit()" aria-hidden="true"></div>

<!-- A11Y.1, 2.4.3. aria-modal tells a screen reader the page behind is
     inert; it does not make it so, and Tab used to walk out into the page.
     cdkTrapFocus keeps Tab inside. Focus moves in on open and goes back to
     the control that opened it on close (the component does both). -->
<div
  #dialog
  class="gh"
  role="dialog"
  aria-modal="true"
  aria-labelledby="gh-t"
  tabindex="-1"
  cdkTrapFocus
>
  <div class="gh-head">
    <span class="gh-tick" aria-hidden="true">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 6L9 17l-5-5" />
      </svg>
    </span>
    <div class="gh-hid">
      <!-- Focused on open, so the dialog's name is the first thing read. -->
      <h2 id="gh-t" #headingEl tabindex="-1">{{ heading }}</h2>
      <p>{{ subheading }}</p>
    </div>
    <button class="gh-x" type="button" aria-label="Close" (click)="closed.emit()">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2.2" stroke-linecap="round" aria-hidden="true">
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </button>
  </div>

  <div class="gh-body">
    <!-- All four, set or not. A member deciding whether to carry on needs the
         state of the others, not praise for the one they just did. -->
    <section class="gh-sec">
      <h3>Your goals</h3>
      <ul class="gh-goals">
        @for (goal of goals; track goal.key) {
          <li
            [class.is-set]="!!goal.value"
            [class.is-next]="goal.isNext && !goal.value"
            [class.is-optional]="goal.isOptional && !goal.value"
          >
            <span class="gh-mk" aria-hidden="true"></span>
            <b>
              {{ goal.label }}
              @if (goal.isOptional) {
                <!-- Said, not implied by the dashes alone: a dotted outline is
                     a hint, and this is a fact the member is entitled to. -->
                <span class="gh-opt-tag">optional</span>
              }
            </b>
            <em>{{ goal.value || (goal.isNext ? 'Next' : goal.isOptional ? 'Not needed for your report' : 'None selected') }}</em>
            @if (goal.value) {
              <button class="gh-rm" type="button" [attr.data-goal-key]="goal.key" (click)="goalRemoved.emit(goal.key)">
                Remove
              </button>
            }
          </li>
        }
      </ul>
    </section>

    <!-- The question setting a civilian goal raises, asked where it is raised.
         CPD-661: of military members only. There is no version of this
         question a civilian can answer, and it used to be required of them. -->
    @if (asksServiceIntent) {
    <!-- WALK-70 (approved 09-29): a required step on its own light blue-grey
         ground, marked with the #17-18 asterisk. STAY_OR_LEAVE_V2 = false in
         the component restores the old section. -->
    <section class="gh-sec" [class.gh-intent]="intentV2">
      @if (intentV2) {
        <h3 id="gh-intent-h" class="gh-intent-h">
          Are you leaving, or staying in?<span class="gh-ast" aria-hidden="true">*</span><span class="gh-sr"> (required)</span>
        </h3>
      } @else {
        <h3>Are you leaving, or staying in?</h3>
      }
      <p class="gh-q">
        Your civilian goal does not have to mean separating. A related military
        occupation can be a path to the same work, with Tuition Assistance
        behind it.
      </p>

      @if (needsAnswer()) {
        <p class="gh-need" role="status">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2.2" stroke-linecap="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4.5" />
            <circle cx="12" cy="16.2" r=".9" fill="currentColor" stroke="none" />
          </svg>
          {{ intentV2 ? 'Choose one to carry on' : 'Answer this to open your Goals Report' }}
        </p>
      }

      <div
        class="gh-opts"
        role="radiogroup"
        [attr.aria-label]="intentV2 ? null : 'Are you leaving or staying in'"
        [attr.aria-labelledby]="intentV2 ? 'gh-intent-h' : null"
        [attr.aria-required]="intentV2 ? true : null"
        (keydown)="onIntentKey($event)"
      >
        <!-- 508 audit (2.1.1, 4.1.2): the radio pattern. One Tab stop, the
             picked answer (the first when there is none), and the arrow keys
             move between the two, picking as they go. -->
        <button
          class="gh-opt"
          type="button"
          role="radio"
          [attr.aria-checked]="intent === 'transition'"
          [attr.tabindex]="intentTabIndex('transition')"
          (click)="pickIntent('transition')"
        >
          <span class="gh-dot" aria-hidden="true"></span>
          <span>
            <b>I want to transition from military service</b>
            <em>Civilian occupations, credentials and schools</em>
          </span>
        </button>
        <button
          class="gh-opt"
          type="button"
          role="radio"
          [attr.aria-checked]="intent === 'stay'"
          [attr.tabindex]="intentTabIndex('stay')"
          (click)="pickIntent('stay')"
        >
          <span class="gh-dot" aria-hidden="true"></span>
          <span>
            <b>I want to stay in the service but still pursue education</b>
            <em>Related military occupations, and Tuition Assistance toward a degree or credential</em>
          </span>
        </button>
      </div>
      <!--
        WALK-71 (approved 09-29) brings the list back, under the answer that
        raises it, and REVERSES the GR-13 note below: Bethany asked that
        "staying in" show the military occupations that lead to the same
        career goal. Only when the host passes the list (relatedOccupations
        not null); STAY_OR_LEAVE_V2 = false removes it with the rest.
      -->
      @if (intentV2 && intent === 'stay' && relatedOccupations !== null) {
        <div class="gh-mocs">
          <h4>Military occupations that lead to {{ careerTitle || 'this career' }}</h4>
          @if (canMatchOccupations === false) {
            <p>These are matched from your Skills Survey. Take it to see the military occupations that fit this goal.</p>
          } @else if (relatedLoading) {
            <p role="status">Loading the military occupations for this goal...</p>
          } @else if (!relatedOccupations.length) {
            <p>No military occupations are matched to this career yet.</p>
          } @else {
            <p>Set one as a goal and it goes on your Goals Report beside this career.</p>
            <ul class="gh-moc-list">
              @for (moc of relatedOccupations; track moc.code) {
                <li class="gh-moc2" [class.is-saved]="moc.isFavorite">
                  <span class="gh-moc-code">{{ moc.code }}</span>
                  <b>{{ moc.title }}</b>
                  <em>{{ moc.branch }}</em>
                  <button
                    class="gh-moc2-set"
                    type="button"
                    [class.is-saved]="moc.isFavorite"
                    [attr.aria-pressed]="!!moc.isFavorite"
                    [attr.aria-label]="(moc.isFavorite ? 'Goal set: ' : 'Set as my goal: ') + moc.title"
                    (click)="occupationToggled.emit(moc)"
                  >
                    {{ moc.isFavorite ? 'Goal set' : 'Set as my goal' }}
                  </button>
                </li>
              }
            </ul>
          }
        </div>
      }
      <!--
        GR-13. The similar-military-occupations block was here, and it is
        removed on purpose.

        It offered a reclassification while the member was in the middle of
        confirming a goal they had just chosen, which is the wrong moment to
        ask "or would you rather do something else in service". Nothing about
        it was broken -- all three of its branches worked, including the
        honest empty state that stopped us conflating "no survey" with
        "survey done, no match". It is gone because it does not belong on
        this screen, not because it failed.

        Deleting the branches, not hiding them: a flagged-off block here
        would be a second thing to reason about every time this dialog
        changes. `relatedOccupations`, `canMatchOccupations` and
        `militaryGoalSet` stay on the component -- the occupation page still
        passes them, and the same list is offered there, where choosing a
        direction is the actual question.
      -->
    </section>
    }
  </div>

  <div class="gh-foot">
    <button class="gh-go" type="button" (click)="continueOn()">
      {{ primaryLabel }}
    </button>
    @if (isComplete) {
      <!-- Site rule (09-29): a tertiary action is the blue text link. -->
      <button class="gh-alt is-link" type="button" (click)="closed.emit()">Not now</button>
    } @else {
      <button class="gh-alt" type="button" (click)="openReport()">
        Open your Goals Report
      </button>
    }
  </div>
</div>
